// =============================================================================
// FILE STATUS HEADER  (lihat /app/memory/MASTER_BLUEPRINT.md A7)
// status   : ACTIVE (Supabase V2) — FITUR BAYAR NANTI (Piutang / AR)
// version  : 1.0
// protected: NO  -> Rekap Piutang: dikelompokkan per "Untuk Siapa", umur utang, pelunasan & force-bill.
// =============================================================================
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Clock, Wallet, Gavel, Loader2, CheckCircle2, RefreshCw, Users, FileText } from "lucide-react";
import { toast } from "sonner";
import { useV2Auth } from "@/v2/V2AuthContext";
import { allowedStores, openPdf } from "@/v2/components/helpers";

const rupiah = (n) => "Rp " + Number(n || 0).toLocaleString("id-ID");
const STORE_LABEL = { STORE_AREMA: "Store Arema (SBY)", STORE_BORNEO: "Store Borneo (SMD)" };
const STORE_BY_ROLE = { STAFF_STORE_AREMA: "STORE_AREMA", STAFF_STORE_BORNEO: "STORE_BORNEO" };
const SETTLE_METHODS = ["Cash", "QRIS", "Debit", "Transfer"];
// Tab status (segmented) — lebih mudah ditemukan daripada dropdown. Default "Belum Lunas".
const STATUS_TABS = [
  { v: "UNPAID", l: "Belum Lunas" },
  { v: "PAID", l: "Lunas" },
  { v: "FORCED", l: "Force-Bill" },
  { v: "ALL", l: "Semua" },
];

const fmtDate = (s) => {
  if (!s) return "-";
  try { return new Date(s).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return s; }
};

// Badge status: UNPAID=AMBER (perlu tindakan), PAID=HIJAU, FORCED=penanda khusus (ungu).
const StatusBadge = ({ status }) => {
  if (status === "PAID")
    return <Badge variant="success" className="gap-1" data-testid="ar-status-paid"><CheckCircle2 className="w-3 h-3" />Lunas</Badge>;
  if (status === "FORCED")
    return <Badge variant="forced" className="gap-1" data-testid="ar-status-forced"><Gavel className="w-3 h-3" />Force-Bill</Badge>;
  return <Badge variant="warning" className="gap-1" data-testid="ar-status-unpaid"><Clock className="w-3 h-3" />Belum Lunas</Badge>;
};

const AgeBadge = ({ days }) => (
  <Badge variant={days >= 30 ? "warning" : "outline"} data-testid="ar-age">
    {days} hari
  </Badge>
);

export default function PiutangPage() {
  const { me, api } = useV2Auth();
  const role = me?.role;
  const isStaffStore = role === "STAFF_STORE_AREMA" || role === "STAFF_STORE_BORNEO";
  const isOwner = role === "OWNER";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("UNPAID");
  const [store, setStore] = useState("ALL");
  const [minAge, setMinAge] = useState("");

  // Aksi (pengaman klik-ganda)
  const [settleTarget, setSettleTarget] = useState(null);
  const [settleMethod, setSettleMethod] = useState("Cash");
  const [forceTarget, setForceTarget] = useState(null);
  const [forceReason, setForceReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      p.set("status", status);
      if (store !== "ALL") p.set("store", store);
      if (String(minAge).trim() !== "") p.set("min_age_days", String(parseInt(minAge, 10) || 0));
      const r = await api(`/api/v2/receivables?${p.toString()}`);
      if (r.ok) setData(await r.json());
      else setData({ groups: [], totals: { count: 0, total_value: 0 } });
    } catch (e) {
      setData({ groups: [], totals: { count: 0, total_value: 0 } });
    } finally { setLoading(false); }
  }, [api, status, store, minAge]);

  useEffect(() => { load(); }, [load]);

  const totals = data?.totals || { count: 0, total_value: 0 };
  const groups = data?.groups || [];

  const canSettle = (inv) => isStaffStore && inv.credit_status === "UNPAID" && inv.store === STORE_BY_ROLE[role];
  const canForce = (inv) => isOwner && inv.credit_status === "UNPAID";

  async function doSettle() {
    if (!settleTarget || busy) return;
    setBusy(true);
    try {
      const r = await api(`/api/v2/receivables/${settleTarget.id}/settle`, {
        method: "POST", body: JSON.stringify({ settle_method: settleMethod }),
      });
      const j = await r.json();
      if (!r.ok) { toast.error(typeof j.detail === "string" ? j.detail : "Gagal melunasi"); return; }
      toast.success(`Pelunasan tercatat · ${settleTarget.invoice_no} · ${rupiah(settleTarget.total)}`);
      setSettleTarget(null); setSettleMethod("Cash");
      // Tetap TAMPILKAN transaksi yang baru lunas (bukti tercatat) -> pindah ke "Semua".
      if (status === "ALL") { await load(); } else { setStatus("ALL"); }
    } finally { setBusy(false); }
  }

  async function doForce() {
    if (!forceTarget || busy) return;
    if (!forceReason.trim()) { toast.error("Alasan wajib diisi"); return; }
    setBusy(true);
    try {
      const r = await api(`/api/v2/receivables/${forceTarget.id}/force-bill`, {
        method: "POST", body: JSON.stringify({ reason: forceReason.trim() }),
      });
      const j = await r.json();
      if (!r.ok) { toast.error(typeof j.detail === "string" ? j.detail : "Gagal force-bill"); return; }
      toast.success(`Force-bill tercatat · ${forceTarget.invoice_no}`);
      setForceTarget(null); setForceReason("");
      // Tetap TAMPILKAN transaksi yang baru force-bill -> pindah ke "Semua".
      if (status === "ALL") { await load(); } else { setStatus("ALL"); }
    } finally { setBusy(false); }
  }

  return (
    <div className="space-y-6" data-testid="v2-piutang-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold flex items-center gap-2"><Wallet className="w-6 h-6 text-[hsl(var(--warning))]" />Rekap Piutang (Bayar Nanti)</h2>
          <p className="text-sm text-muted-foreground">Transaksi sudah keluar barang, menunggu pelunasan. Belum masuk penagihan sampai lunas.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="ar-refresh">
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />Muat ulang
        </Button>
      </div>

      {/* Filter */}
      <Card className="p-4 rounded-2xl grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="ar-filters">
        <div className="sm:col-span-2 lg:col-span-2"><Label className="text-xs">Status</Label>
          <div className="flex flex-wrap gap-1.5 mt-1" data-testid="ar-filter-status" role="group" aria-label="Filter status piutang">
            {STATUS_TABS.map((t) => (
              <Button
                key={t.v}
                type="button"
                size="sm"
                variant={status === t.v ? "default" : "outline"}
                onClick={() => setStatus(t.v)}
                data-testid={`ar-status-tab-${t.v}`}
                className={status === t.v ? "" : "text-muted-foreground"}
              >
                {t.l}
              </Button>
            ))}
          </div>
        </div>
        {!isStaffStore && (
          <div><Label className="text-xs">Store / Region</Label>
            <Select value={store} onValueChange={setStore}>
              <SelectTrigger data-testid="ar-filter-store"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Semua (sesuai region)</SelectItem>
                {allowedStores(me).map((s) => <SelectItem key={s} value={s}>{STORE_LABEL[s] || s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div><Label className="text-xs">Umur minimal (hari)</Label>
          <Input type="number" min="0" value={minAge} onChange={(e) => setMinAge(e.target.value)}
            placeholder="mis. 30" data-testid="ar-filter-age" />
        </div>
      </Card>

      {/* Totals */}
      <div className="grid grid-cols-2 gap-3">
        <Card className="p-4 rounded-2xl" data-testid="ar-total-count">
          <p className="text-xs text-muted-foreground">Jumlah Transaksi</p>
          <p className="text-2xl font-bold tabular-nums">{totals.count}</p>
        </Card>
        <Card className="p-4 rounded-2xl" data-testid="ar-total-value">
          <p className="text-xs text-muted-foreground">Total Nilai</p>
          <p className="text-2xl font-bold tabular-nums text-[hsl(var(--warning))]">{rupiah(totals.total_value)}</p>
        </Card>
      </div>

      {/* Groups per "Untuk Siapa" */}
      {loading && !data ? (
        <div className="space-y-3"><Skeleton className="h-32" /><Skeleton className="h-32" /></div>
      ) : groups.length === 0 ? (
        <Card className="p-10 rounded-2xl text-center space-y-2" data-testid="ar-empty">
          <CheckCircle2 className="w-10 h-10 text-[hsl(var(--success))] mx-auto" />
          <p className="font-medium">Tidak ada piutang untuk filter ini</p>
          <p className="text-sm text-muted-foreground">Semua transaksi sudah beres. 🎉</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {groups.map((g) => (
            <Card key={g.credit_for} className="p-4 rounded-2xl" data-testid="ar-group">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-2 min-w-0">
                  <Users className="w-4 h-4 text-primary shrink-0" />
                  <span className="font-semibold truncate" data-testid="ar-group-name">{g.credit_for}</span>
                  <Badge variant="secondary">{g.count} transaksi</Badge>
                </div>
                <div className="flex items-center gap-2">
                  {g.max_age_days >= 30 && <Badge variant="warning">tertua {g.max_age_days} hari</Badge>}
                  <span className="font-bold text-[hsl(var(--warning))] tabular-nums">{rupiah(g.total)}</span>
                </div>
              </div>
              <div className="space-y-2">
                {g.invoices.map((inv) => (
                  <div key={inv.id} data-testid="ar-invoice-row"
                    className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 p-3 rounded-xl border">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">{inv.invoice_no}</span>
                        <StatusBadge status={inv.credit_status} />
                        {inv.credit_status === "UNPAID" && <AgeBadge days={inv.age_days} />}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {inv.store} · transaksi {fmtDate(inv.txn_datetime)}
                      </p>
                      {(inv.credit_status === "PAID" || inv.credit_status === "FORCED") && inv.paid_at && (
                        <p
                          className={`text-xs mt-0.5 font-medium ${inv.credit_status === "PAID" ? "text-[hsl(var(--success))]" : "text-[hsl(var(--forced))]"}`}
                          data-testid="ar-paid-date"
                        >
                          {inv.credit_status === "PAID"
                            ? `Dibayar ${fmtDate(inv.paid_at)}${inv.settle_method ? ` · ${inv.settle_method}` : ""}`
                            : `Force-bill ${fmtDate(inv.paid_at)}`}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center justify-between sm:justify-end gap-3">
                      <span className="font-semibold tabular-nums">{rupiah(inv.total)}</span>
                      {canSettle(inv) && (
                        <Button size="sm" className="bg-[hsl(var(--success))] hover:bg-[hsl(var(--success))]/90 text-[hsl(var(--success-foreground))]"
                          onClick={() => { setSettleTarget(inv); setSettleMethod("Cash"); }}
                          data-testid="ar-settle-btn">
                          <Wallet className="w-4 h-4 mr-1" />Terima Pembayaran
                        </Button>
                      )}
                      {canForce(inv) && (
                        <Button size="sm" variant="outline" className="border-[hsl(var(--forced))] text-[hsl(var(--forced))] hover:bg-[hsl(var(--forced))]/10"
                          onClick={() => { setForceTarget(inv); setForceReason(""); }}
                          data-testid="ar-force-btn">
                          <Gavel className="w-4 h-4 mr-1" />Force Bill
                        </Button>
                      )}
                      {inv.credit_status === "PAID" && (
                        <Button size="sm" variant="outline"
                          onClick={() => openPdf(api, "pelunasan", inv.id, inv.settle_receipt_no || inv.invoice_no)}
                          data-testid="ar-receipt-btn">
                          <FileText className="w-4 h-4 mr-1" />Bukti Lunas (PDF)
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Konfirmasi Pelunasan (menyentuh uang -> wajib konfirmasi) */}
      <AlertDialog open={!!settleTarget} onOpenChange={(o) => { if (!o && !busy) setSettleTarget(null); }}>
        <AlertDialogContent data-testid="ar-settle-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Terima Pembayaran Piutang?</AlertDialogTitle>
            <AlertDialogDescription>
              Invoice <b>{settleTarget?.invoice_no}</b> · untuk <b>{settleTarget?.credit_for}</b> ·
              senilai <b>{rupiah(settleTarget?.total)}</b>. Setelah dilunasi, transaksi ini akan masuk
              penagihan pada periode pembayaran. Tindakan ini tidak bisa dibatalkan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-1">
            <Label className="text-xs">Metode Pembayaran</Label>
            <Select value={settleMethod} onValueChange={setSettleMethod}>
              <SelectTrigger data-testid="ar-settle-method"><SelectValue /></SelectTrigger>
              <SelectContent>{SETTLE_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); doSettle(); }} disabled={busy}
              className="bg-[hsl(var(--success))] hover:bg-[hsl(var(--success))]/90 text-[hsl(var(--success-foreground))]" data-testid="ar-settle-confirm">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Wallet className="w-4 h-4 mr-1" />}Konfirmasi Lunas
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Force Bill (Owner) — alasan wajib + audit */}
      <Dialog open={!!forceTarget} onOpenChange={(o) => { if (!o && !busy) setForceTarget(null); }}>
        <DialogContent data-testid="ar-force-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Gavel className="w-5 h-5 text-[hsl(var(--forced))]" />Force Bill Piutang</DialogTitle>
            <DialogDescription>
              Memaksa piutang <b>{forceTarget?.invoice_no}</b> (untuk <b>{forceTarget?.credit_for}</b>) masuk
              penagihan periode berjalan saat klub mengambil tanggung jawab. Klub tetap bertanggung jawab.
              Alasan tertulis WAJIB dan tercatat pada audit.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs">Alasan <span className="text-[hsl(var(--destructive))]">*</span></Label>
            <Textarea value={forceReason} onChange={(e) => setForceReason(e.target.value)}
              placeholder="Tuliskan alasan force-bill (wajib)" data-testid="ar-force-reason" rows={3} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setForceTarget(null)} disabled={busy}>Batal</Button>
            <Button onClick={doForce} disabled={busy || !forceReason.trim()}
              className="bg-[hsl(var(--forced))] hover:bg-[hsl(var(--forced))]/90 text-[hsl(var(--forced-foreground))]" data-testid="ar-force-confirm">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Gavel className="w-4 h-4 mr-1" />}Force Bill
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
